import {
  ORGANIZATION_TYPE,
  PORTAL_OFFICE_TYPE,
  PORTAL_USE_TYPE,
  USER_ROLE_TYPE,
  isPortalUseType,
} from '@/types/types';
import { useSession, signIn, signOut } from 'next-auth/react';
import { Session } from 'next-auth';
import { ibowClient } from '@/init/ibowClient';
import { ChangeStaffReq } from '@/init/ibowClient/models/ChangeStaffReq';
import { apiRequest, clearToken, setToken } from './apiRequestHelper';
import { ReadonlyURLSearchParams } from 'next/navigation';
import { api_common_responseGetIbowStaffInfo } from '@/init/swagger';
const BASE_URL = process.env.NEXT_PUBLIC_SITE_URL;

export async function createPortalSession(
  searchParams: ReadonlyURLSearchParams,
  session: Session | null,
  useType?: PORTAL_USE_TYPE,
  callbackUrl?: string,
) {
  // Client Componentからの呼び出し専用
  const paramsUseType = searchParams.get('use_type') || '0';
  const correctUseType = paramsUseType
    ? isPortalUseType(parseInt(paramsUseType))
    : false;
  const sessionUseType = correctUseType
    ? parseInt(paramsUseType)
    : session?.user?.use_type || useType || undefined;
  const result = await signIn('portal-credentials', {
    use_type: sessionUseType,
    redirect: false,
  });
  if (!!result?.error) {
    await clearToken();
    window.location.href = `/signin?use_type=${sessionUseType}`;
  } else {
    const url = callbackUrl
      ? `${BASE_URL}${callbackUrl}`
      : `${BASE_URL}/recept`;
    window.location.href = url;
  }
}

export async function deletePortalSession() {
  // Client Componentからの呼び出し専用
  if (typeof window !== 'undefined') {
    signOut({ redirect: false });
  }
  return;
}

export async function logout(useType?: PORTAL_USE_TYPE | null) {
  if (typeof window !== 'undefined') {
    await clearToken();
    await deletePortalSession();
    if (useType) {
      window.location.href = `/signin?use_type=${useType}`;
    } else {
      window.location.href = '/signin';
    }
  }
  return;
}

export async function changeStaff({
  portalStaff,
  requestUser,
}: {
  portalStaff?: {
    portalOfficeStaffId: number;
    portalOfficeId: number;
    ibowOfficeId: number;
    ibowStaffId: number;
  };
  requestUser?: {
    portalRequestUserId: number;
    organizationId: number;
    ibowStaffId: number;
  };
}) {
  if (!portalStaff && !requestUser) {
    await clearToken();
    return { error: true };
  }

  let requestBody: ChangeStaffReq = {
    portal_office_staff_id: 0,
    portal_request_user_id: 0,
    portal_office_id: 0,
    organization_id: 0,
    portal_office_staff_role: 0,
    portal_request_user_role: 0,
    ibow_office_id: 0,
    ibow_staff_id: 0,
  };

  if (portalStaff) {
    requestBody = {
      portal_office_staff_id: portalStaff.portalOfficeStaffId,
      portal_request_user_id: 0,
      portal_office_id: portalStaff.portalOfficeId,
      organization_id: 0,
      portal_office_staff_role: 1,
      portal_request_user_role: 0,
      ibow_office_id: portalStaff.ibowOfficeId,
      ibow_staff_id: portalStaff.ibowStaffId,
    };
  }
  if (requestUser) {
    requestBody = {
      portal_office_staff_id: 0,
      portal_request_user_id: requestUser.portalRequestUserId,
      portal_office_id: 0,
      organization_id: requestUser.organizationId,
      portal_office_staff_role: 0,
      portal_request_user_role: 1,
      ibow_office_id: 0,
      ibow_staff_id: requestUser.ibowStaffId,
    };
  }

  const res = await apiRequest({
    requestFunc: () =>
      ibowClient.authentication.postChangeStaff({
        body: requestBody,
      }),
  });

  if (!!res?.token && !!res?.ref_token) {
    await setToken({ token: res?.token, refToken: res?.ref_token });
    return { ok: true };
  } else {
    await clearToken();
    return { error: true };
  }
}

export function isAdminRole() {
  // Client Componentからの呼び出し専用
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data: session } = useSession();

  return getIsAdminRoleBySession(session);
}

export const getIsAdminRoleBySession = (session: Session | null) => {
  const loginAccount = getLoginAccount(session);
  if (!loginAccount?.role) {
    return false;
  }

  return (
    loginAccount?.role === USER_ROLE_TYPE.ADMIN ||
    loginAccount?.role === USER_ROLE_TYPE.SUPER_ADMIN
  );
};

export async function getCurrentOfficeOrOrgName() {
  let session;
  if (typeof window === 'undefined') {
    const { auth } = await import('@/init/auth');
    session = await auth();
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data } = useSession();
    session = data;
  }

  return (
    session?.user?.portal_staff?.portal_office?.name ||
    session?.user?.request_user?.organization?.name
  );
}

function getLoginAccount(session: Session | null) {
  return session?.user?.portal_staff || session?.user?.request_user;
}

type Payload = {
  admin_id?: number;
  agreement_id?: number;
  all_agreement_ids?: number[];
  all_office_ids?: number[];
  all_staff_ids?: number[];
  auth_id?: number;
  co_code?: string;
  ewell_id?: number;
  exp?: number;
  group_office_ids?: number[];
  is_bi_enabled?: number;
  is_honobono?: number;
  office_id?: number;
  office_staff_id?: number;
  organization_id?: number;
  parent_office_id?: number;
  portal_office_id?: number;
  portal_office_staff_role?: number;
  portal_request_user_id?: number;
  portal_request_user_role?: number;
  portal_staff_id?: number;
  ibow_office_id?: number;
  ibow_staff_id?: number;
  proxy_flg?: number;
  receipt_office_id?: number;
  site_name?: string;
  sleep_office_ids?: number[];
  staff_div?: number;
};

export function getPayload(token: string) {
  const base64Url = token.split('.')[1];
  if (!base64Url) {
    return null;
  }
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );
  return JSON.parse(jsonPayload) as Payload;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const checkSameUser = (payload: any, session: Session | null) => {
  const useType = session?.user?.use_type;
  if (useType === PORTAL_USE_TYPE.PORTAL_STAFF) {
    if (!payload?.portal_staff_id) {
      return !session?.user?.portal_staff?.id;
    }
    return payload?.portal_staff_id === session?.user?.portal_staff?.id;
  }
  if (useType === PORTAL_USE_TYPE.REQUEST_USER) {
    if (!payload?.portal_request_user_id) {
      return !session?.user?.request_user?.id;
    }
    return payload?.portal_request_user_id === session?.user?.request_user?.id;
  }
  return !payload?.portal_staff_id && !payload?.portal_request_user_id;
};

type GetDisplayNameForRequestUserWhenCreateAccountArgs = {
  session: Session | null;
};
export const getDisplayNameForRequestUserWhenCreateAccount = ({
  session,
}: GetDisplayNameForRequestUserWhenCreateAccountArgs) => {
  return {
    display_last_name: session?.user?.ewell_user?.family_name || '',
    display_last_name_furigana:
      session?.user?.ewell_user?.family_name_furigana || '',
    display_first_name: session?.user?.ewell_user?.first_name || '',
    display_first_name_furigana:
      session?.user?.ewell_user?.first_name_furigana || '',
  };
};

type GetDisplayNameForPortalStaffWhenCreateAccountArgs = {
  session: Session | null;
  ibowStaffInfo: void | api_common_responseGetIbowStaffInfo | null;
};
export const getDisplayNameForPortalStaffWhenCreateAccount = ({
  session,
  ibowStaffInfo,
}: GetDisplayNameForPortalStaffWhenCreateAccountArgs) => {
  const isIbowStaffInputName =
    !!ibowStaffInfo?.ibow_staff_info?.ibow_nickname_last_name;

  if (isIbowStaffInputName) {
    return {
      display_family_name:
        ibowStaffInfo?.ibow_staff_info?.ibow_nickname_last_name || '',
      display_family_name_furigana:
        ibowStaffInfo?.ibow_staff_info?.ibow_nickname_furigana_last_name || '',
      display_first_name:
        ibowStaffInfo?.ibow_staff_info?.ibow_nickname_first_name || '',
      display_first_name_furigana:
        ibowStaffInfo?.ibow_staff_info?.ibow_nickname_furigana_first_name || '',
    };
  }

  return {
    display_family_name: session?.user?.ewell_user?.family_name || '',
    display_family_name_furigana:
      session?.user?.ewell_user?.family_name_furigana || '',
    display_first_name: session?.user?.ewell_user?.first_name || '',
    display_first_name_furigana:
      session?.user?.ewell_user?.first_name_furigana || '',
  };
};

export const getPortalOfficeLabels = (
  portalOfficeType: PORTAL_OFFICE_TYPE,
): {
  portalOfficeInfoLabel: string;
  portalOfficeNameLabel: string;
  portalOfficeCodeLabel: string;
} => {
  switch (portalOfficeType) {
    case PORTAL_OFFICE_TYPE.NURSING_STATION:
      return {
        portalOfficeInfoLabel: 'ステーション情報',
        portalOfficeNameLabel: '事業所名',
        portalOfficeCodeLabel: '事業所番号',
      };
    case PORTAL_OFFICE_TYPE.HOSPITAL:
      return {
        portalOfficeInfoLabel: '医療機関情報',
        portalOfficeNameLabel: '医療機関名',
        portalOfficeCodeLabel: '医療機関コード',
      };
    case PORTAL_OFFICE_TYPE.CARE_GIVING_OFFICE:
      throw Error("unused type 'CARE_GIVING_OFFICE'");
  }
};

export const getOrganizationLabels = (
  orgType: ORGANIZATION_TYPE,
): {
  organizationInfoLabel: string;
  organizationNameLabel: string;
  organizationCodeLabel: string;
} => {
  switch (orgType) {
    case ORGANIZATION_TYPE.NURSING_STATION:
      return {
        organizationInfoLabel: 'ステーション情報',
        organizationNameLabel: '事業所名',
        organizationCodeLabel: '事業所番号',
      };
    case ORGANIZATION_TYPE.HOSPITAL:
      return {
        organizationInfoLabel: '医療機関情報',
        organizationNameLabel: '医療機関名',
        organizationCodeLabel: '医療機関コード',
      };
    case ORGANIZATION_TYPE.CARE_GIVING_OFFICE:
      return {
        organizationInfoLabel: '介護事業所・施設情報',
        organizationNameLabel: '事業所・施設名',
        organizationCodeLabel: '介護保険事業者番号',
      };
  }
};
